/**
 * Pipeline Animation Block JavaScript
 *
 * Starts the DotLottie diagram when the player scrolls into view.
 * @lottiefiles/dotlottie-wc exposes the engine as el.dotLottie (play() has no args).
 */

(function () {
    'use strict';

    const started = new WeakSet();
    const SELECTOR = '.pipeline-animation dotlottie-wc[data-play-on-view]';

    function getEngine(el) {
        return el.dotLottie || null;
    }

    function playEngine(el) {
        if (started.has(el)) {
            return true;
        }

        const engine = getEngine(el);
        if (!engine || typeof engine.play !== 'function') {
            return false;
        }

        if ('isLoaded' in engine && !engine.isLoaded) {
            return false;
        }

        started.add(el);

        try {
            engine.play();
        } catch (e) {
            started.delete(el);
            return false;
        }

        return true;
    }

    function playWhenReady(el) {
        if (started.has(el)) {
            return;
        }

        const onReady = () => {
            playEngine(el);
        };

        const bind = (attempt) => {
            const engine = getEngine(el);

            if (engine) {
                if (typeof engine.addEventListener === 'function') {
                    engine.addEventListener('load', onReady);
                    engine.addEventListener('ready', onReady);
                }

                if (playEngine(el)) {
                    return;
                }

                return;
            }

            if (attempt < 80) {
                window.setTimeout(() => bind(attempt + 1), 50);
            }
        };

        const startBind = () => bind(0);

        if (window.customElements && typeof customElements.whenDefined === 'function') {
            customElements.whenDefined('dotlottie-wc').then(startBind).catch(startBind);
        } else {
            startBind();
        }
    }

    function initPipelineAnimation(container) {
        const root = container || document;
        const players = root.querySelectorAll(SELECTOR);

        if (!players.length) {
            return;
        }

        if (!('IntersectionObserver' in window)) {
            players.forEach(playWhenReady);
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                observer.unobserve(entry.target);
                playWhenReady(entry.target);
            });
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0,
        });

        players.forEach((player) => {
            observer.observe(player);
        });
    }

    function onPageReady(e) {
        const container = e?.detail?.container || document;
        if (container.querySelector('.pipeline-animation')) {
            initPipelineAnimation(container);
        }
    }

    if (document.body?.hasAttribute?.('data-barba')) {
        document.addEventListener('barba:pageReady', onPageReady);
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => onPageReady({ detail: { container: document } }));
    } else {
        onPageReady({ detail: { container: document } });
    }
})();
